import { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Printer, RefreshCw, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/kit";
import { printMenuPoster } from "@/lib/allergenPrint";

export function MenuShareCard() {
  const { can, business } = useAuth();
  const { data, setData } = useApi("/allergen-menu/settings");
  const [busy, setBusy] = useState(false);
  const qr = useRef(null);
  if (!data) return null;
  const admin = can("admin") && !business?.is_demo;
  const url = data.token ? `${window.location.origin}/menu/${data.token}` : "";
  const call = async (fn, msg) => {
    setBusy(true);
    try {
      const { data: d } = await fn();
      setData(d);
      toast.success(msg);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card data-testid="menu-share-card" className="no-print mb-6">
      <div className="flex flex-wrap items-start gap-6">
        <div className="min-w-[240px] flex-1">
          <h3 className="flex items-center gap-2 text-lg font-bold"><QrCode className="h-5 w-5" />Allergen menu for customers</h3>
          <p className="mt-1 text-sm text-slate-500">A public page customers open by scanning a QR code. It updates the moment you change this matrix.</p>
          {admin && <button data-testid="menu-toggle" disabled={busy} onClick={() => call(() => api.put("/allergen-menu/settings", { enabled: !data.enabled }), data.enabled ? "Customer menu turned off" : "Customer menu is live")}
            className={`ka-press mt-4 rounded-full px-5 py-2.5 text-sm font-semibold ${data.enabled ? "border border-slate-300 bg-white text-slate-700" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}>{data.enabled ? "Turn off" : "Turn on customer menu"}</button>}
          {!admin && !data.enabled && <p className="mt-3 text-sm text-slate-500">Ask the owner or a manager to turn it on.</p>}
          {data.enabled && url && <div className="mt-4 space-y-2">
            <div className="flex gap-2"><a data-testid="menu-public-link" href={url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate rounded-lg bg-slate-100 px-3 py-2 font-mono text-xs text-sky-800 underline">{url}</a><button data-testid="menu-copy-link" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }} className="ka-press rounded-full border border-slate-300 px-3"><Copy className="h-4 w-4" /></button></div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="menu-print-poster" onClick={() => { if (!printMenuPoster(business?.name || "", qr.current?.innerHTML || "", url)) toast.error("Allow pop-ups to print"); }} className="ka-press inline-flex items-center gap-1 rounded-full bg-slate-950 px-4 py-2 text-xs font-semibold text-white"><Printer className="h-3.5 w-3.5" />Print QR poster</button>
              {admin && <button data-testid="menu-new-link" disabled={busy} onClick={() => window.confirm("Make a new link? Printed QR codes will stop working.") && call(() => api.post("/allergen-menu/new-link"), "New link made. Reprint your QR codes.")} className="ka-press inline-flex items-center gap-1 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold"><RefreshCw className="h-3.5 w-3.5" />New link</button>}
            </div>
          </div>}
        </div>
        {data.enabled && url && <div ref={qr} data-testid="menu-qr" className="rounded-xl bg-white p-3 ring-1 ring-slate-200"><QRCodeSVG value={url} size={136} level="M" /></div>}
      </div>
    </Card>
  );
}
